import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { CampaignBannerViewModel } from '../models/CampaignBannerViewModel';
import type { CampaignDetailsLandingViewModel } from '../models/CampaignDetailsLandingViewModel';
import type { CampaignPromotionListViewModel } from '../models/CampaignPromotionListViewModel';
import type { CampaignPromotionViewModel } from '../models/CampaignPromotionViewModel';
import type { CategoryPromotionViewModel } from '../models/CategoryPromotionViewModel';
import type { CorporateAboutUsPageViewModel } from '../models/CorporateAboutUsPageViewModel';
import type { CorporateAgenciesPageViewModel } from '../models/CorporateAgenciesPageViewModel';
import type { CorporateSeturYayinlariPageViewModel } from '../models/CorporateSeturYayinlariPageViewModel';
import type { FlightLandingViewModel } from '../models/FlightLandingViewModel';
import type { FooterViewModel } from '../models/FooterViewModel';
import type { HeaderViewModel } from '../models/HeaderViewModel';
import type { HeroCampaignViewModel } from '../models/HeroCampaignViewModel';
import type { HotelLandingViewModel } from '../models/HotelLandingViewModel';
import type { LoginSignUpViewModel } from '../models/LoginSignUpViewModel';
import type { PaymentOptionsDetailViewModel } from '../models/PaymentOptionsDetailViewModel';
import type { ProductPromotionViewModel } from '../models/ProductPromotionViewModel';
import type { SetairPageViewModel } from '../models/SetairPageViewModel';
import type { SeturVizePageViewModel } from '../models/SeturVizePageViewModel';
import type { StaticPagesSidebarViewModel } from '../models/StaticPagesSidebarViewModel';
import type { StaticPagesViewModel } from '../models/StaticPagesViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class ContentsService {
  /**
   * Header
   * Gets header
   * @returns HeaderViewModel Success
   * @throws ApiError
   */
  public static async header(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HeaderViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HeaderViewModel>({
      method: 'GET',
      url: `/contents/header`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaigns Banner
   * Gets banner of campaign
   * @returns CampaignBannerViewModel Success
   * @throws ApiError
   */
  public static async campaignsBanner(
    {
      position,
      type,
    }: {
      /**
       * Position: top or bottom, Default: top
       */
      position?: string;
      /**
       * Type: hotel,tour,flight, mainpage default: mainpage
       */
      type?: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CampaignBannerViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CampaignBannerViewModel>({
      method: 'GET',
      url: `/contents/campaigns/banner`,
      query: {
        ...(position ? { position: position } : {}),
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaigns Promotion
   * Gets promotion of campaign
   * @returns CampaignPromotionListViewModel Success
   * @throws ApiError
   */
  public static async campaignsPromotion(
    {
      page,
      size,
    }: {
      page?: number;
      size?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CampaignPromotionListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CampaignPromotionListViewModel>({
      method: 'GET',
      url: `/contents/campaigns/promotion`,
      query: {
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaigns Hero
   * Gets hero banner of campaign
   * @returns HeroCampaignViewModel Success
   * @throws ApiError
   */
  public static async campaignsHero(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HeroCampaignViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HeroCampaignViewModel>({
      method: 'GET',
      url: `/contents/campaigns/hero`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Promotions Product
   * Gets promotions of product
   * @returns ProductPromotionViewModel Success
   * @throws ApiError
   */
  public static async promotionsProduct(
    {
      type,
    }: {
      /**
       * Type: hotel or tour, default: hotel
       */
      type: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<ProductPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<ProductPromotionViewModel>({
      method: 'GET',
      url: `/contents/promotions/product`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Category Promotions
   * Get category promotions by type
   * @returns CategoryPromotionViewModel Success
   * @throws ApiError
   */
  public static async categoryPromotions(
    {
      type,
    }: {
      /**
       * Type: hotel, expedia hotels, tour or cruise default: hotel
       */
      type: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CategoryPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CategoryPromotionViewModel>({
      method: 'GET',
      url: `/contents/promotions/category`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Footer
   * Gets footer
   * @returns FooterViewModel Success
   * @throws ApiError
   */
  public static async footer(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FooterViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FooterViewModel>({
      method: 'GET',
      url: `/contents/footer`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Sidebar
   * Gets sidebar for static pages,
   * @returns StaticPagesSidebarViewModel Success
   * @throws ApiError
   */
  public static async getSidebar(
    {
      type,
    }: {
      /**
       * Type: setur, corporate
       */
      type?: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<StaticPagesSidebarViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<StaticPagesSidebarViewModel>({
      method: 'GET',
      url: `/contents/sidebar`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Document
   * Gets document url or text by type
   * @returns string Success
   * @throws ApiError
   */
  public static async getDocument(
    {
      type,
    }: {
      /**
       * Document Type (AgreementDoc, ConsentDoc, ConsentText)
       */
      type: any;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<string>({
      method: 'GET',
      url: `/contents/document`,
      query: {
        ...(type ? { type: type } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages
   * Gets static pages by slug
   * @returns StaticPagesViewModel Success
   * @throws ApiError
   */
  public static async getStaticPages(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<StaticPagesViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<StaticPagesViewModel>({
      method: 'GET',
      url: `/contents/static`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages (Setair)
   * Gets static page for setair
   * @returns SetairPageViewModel Success
   * @throws ApiError
   */
  public static async getSetair(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SetairPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SetairPageViewModel>({
      method: 'GET',
      url: `/contents/static/setair`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages (Setur Vize)
   * Gets static page for setur vize
   * @returns SeturVizePageViewModel Success
   * @throws ApiError
   */
  public static async getSeturVize(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<SeturVizePageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<SeturVizePageViewModel>({
      method: 'GET',
      url: `/contents/static/setur-vize`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages (Hakkimizda)
   * Gets static page for about us
   * @returns CorporateAboutUsPageViewModel Success
   * @throws ApiError
   */
  public static async getAboutUs(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CorporateAboutUsPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CorporateAboutUsPageViewModel>({
      method: 'GET',
      url: `/contents/static/hakkimizda`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages (Agencies)
   * Gets static page for agencies
   * @returns CorporateAgenciesPageViewModel Success
   * @throws ApiError
   */
  public static async getAgencies(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CorporateAgenciesPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CorporateAgenciesPageViewModel>({
      method: 'GET',
      url: `/contents/static/agencies`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Static Pages (Setur Yayinlari)
   * Gets static page for setur yayinlari
   * @returns CorporateSeturYayinlariPageViewModel Success
   * @throws ApiError
   */
  public static async getSeturYayinlari(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CorporateSeturYayinlariPageViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CorporateSeturYayinlariPageViewModel>({
      method: 'GET',
      url: `/contents/static/seturyayinlari`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Login Signup
   * Gets content for login-signup pages
   * @returns LoginSignUpViewModel Success
   * @throws ApiError
   */
  public static async getLoginSignup(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<LoginSignUpViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<LoginSignUpViewModel>({
      method: 'GET',
      url: `/contents/loginsignup`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Hotels (Landing)
   * Gets hotel landing pages by slug
   * @returns HotelLandingViewModel Success
   * @throws ApiError
   */
  public static async getHotelLanding(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<HotelLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<HotelLandingViewModel>({
      method: 'GET',
      url: `/contents/landing/hotel`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flights (Landing)
   * Gets flight landing pages by slug
   * @returns FlightLandingViewModel Success
   * @throws ApiError
   */
  public static async getFlightLanding(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FlightLandingViewModel>({
      method: 'GET',
      url: `/contents/landing/flight`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Flight-City (Landing)
   * Gets flight-city landing pages by slug
   * @returns FlightLandingViewModel Success
   * @throws ApiError
   */
  public static async getFlightCityLanding(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<FlightLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<FlightLandingViewModel>({
      method: 'GET',
      url: `/contents/landing/flight-city`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaigns (Landing)
   * Gets campaigns for landing pages by slug
   * @returns CampaignPromotionViewModel Success
   * @throws ApiError
   */
  public static async getCampaigns(
    {
      slug,
    }: {
      /**
       * Examples: turk-telekom-kampanyalari
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CampaignPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CampaignPromotionViewModel>({
      method: 'GET',
      url: `/contents/campaigns`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Campaign Details (Landing)
   * Gets campaign detail landing pages by slug
   * @returns CampaignDetailsLandingViewModel Success
   * @throws ApiError
   */
  public static async getCampaignDetails(
    {
      slug,
    }: {
      /**
       * Examples: kismi-odeme-kampanyasi
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CampaignDetailsLandingViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CampaignDetailsLandingViewModel>({
      method: 'GET',
      url: `/contents/campaigns/detail`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Payment Options Details (Landing)
   * Gets payment options details landing pages by slug
   * @returns PaymentOptionsDetailViewModel Success
   * @throws ApiError
   */
  public static async getPaymentDetails(
    {
      slug,
    }: {
      /**
       * Examples: odeme-secenekleri-akbank, odeme-secenekleri-yapi-kredi
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<PaymentOptionsDetailViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<PaymentOptionsDetailViewModel>({
      method: 'GET',
      url: `/contents/payments/detail`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Payment Options
   * Gets payment options
   * @returns CampaignPromotionViewModel Success
   * @throws ApiError
   */
  public static async getPayments(
    {
      slug,
    }: {
      /**
       * Examples: odeme-secenekleri
       */
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<CampaignPromotionViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<CampaignPromotionViewModel>({
      method: 'GET',
      url: `/contents/payments`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Seo (Main Page)
   * Gets seo content for main page
   * @returns string Success
   * @throws ApiError
   */
  public static async getMainPageSeo(
    {
      slug,
    }: {
      slug: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<string> {
    const { cookies = {}, headers = {} } = options ?? {};
    delete cookies.token;
    const result = await __request<string>({
      method: 'GET',
      url: `/contents/seo`,
      query: {
        ...(slug ? { slug: slug } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(headers, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
